import { afterNextRender, Component, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { assertSuccessfulOperationResponse, ErrorReportOperation, FileResult, UploadInfo, UploadService } from '@imagise/entities';
import {
  AnalyticsService,
  type AnalyticsUploadSource,
  DeviceDetectorService,
  formatShortcut,
  getAcceptedClipboardFiles,
  isEditableTarget,
  SpinnerComponent,
} from '@imagise/shared';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { FileDropDirective } from '../../../file-drop';
import { getEditorAnalyticsErrorCategory } from '../../domain/editor-analytics';
import { ErrorReportingService } from '../../../error-reporting/error-reporting.service';

interface UploadAnalyticsAttempt {
  readonly source: AnalyticsUploadSource;
  started: boolean;
  settled: boolean;
}

@Component({
  selector: 'lib-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  imports: [MatIconModule, TranslateModule, FileDropDirective, SpinnerComponent],
  host: { '(document:paste)': 'handlePaste($event)' },
})
export class FileUploaderComponent {
  readonly toolSlug = input.required<string>();

  readonly allowLocalFileUpload = input(false);

  /**
   * When set, the uploader accepts several images at once: the first file
   * becomes the editor source and the rest are emitted via extraFilesSelected
   * (the Animation Maker turns them into frames).
   */
  readonly multiple = input(false);

  /**
   * On file uploaded event
   */
  readonly fileUploaded = output<FileResult>();

  /**
   * Upload process event
   */
  readonly uploadProcess = output<boolean>();

  /** Files beyond the first one in a multi-file selection. */
  readonly extraFilesSelected = output<File[]>();

  /**
   * File input DOM element reference
   */
  readonly fileInput = viewChild.required<ElementRef>('fileInput');

  readonly localFileInput = viewChild<ElementRef<HTMLInputElement>>('localFileInput');

  private readonly uploadService = inject(UploadService);
  private readonly analytics = inject(AnalyticsService);
  private readonly deviceDetector = inject(DeviceDetectorService);
  private readonly errorReporting = inject(ErrorReportingService);

  /** Guards the paste path against starting a second upload mid-flight. */
  protected isUploading = false;

  /**
   * Paste-shortcut label for the uploader hint. Starts with the non-Apple
   * spelling so the SSR markup stays deterministic; the platform-specific
   * label is resolved after hydration.
   */
  readonly pasteShortcut = signal('Ctrl+V');

  constructor() {
    afterNextRender(() =>
      this.pasteShortcut.set(formatShortcut('Mod+V', this.deviceDetector.isApplePlatform()))
    );
  }

  /**
   * Gets upload ID
   */
  get uploadId(): string {
    return this.uploadService.uploadId;
  }

  /**
   * Gets accepted extensions
   */
  get acceptedExtensions(): string {
    return 'image/*';
  }

  handleInputEvent(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files) this.handleFileInput(files, 'picker');
  }

  openLocalFilePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.localFileInput()?.nativeElement.click();
  }

  handleLocalInputEvent(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files) this.handleLocalFileInput(files);
  }

  /**
   * Uploads an image pasted from the clipboard (e.g. a screenshot) through
   * the same flow as a dropped file. Yields to paste into editable fields
   * and to an upload that is already running.
   */
  protected handlePaste(event: ClipboardEvent): void {
    if (this.isUploading || isEditableTarget(event.target)) {
      return;
    }

    const files = getAcceptedClipboardFiles(event, this.acceptedExtensions);
    if (!files.length) {
      return;
    }

    event.preventDefault();
    this.handleFileInput(files, 'paste');
  }

  /**
   * Uploads the first file of the list to the regular cloud endpoint.
   * Failed uploads can be retried by choosing the file again.
   */
  handleFileInput(
    files: ArrayLike<File>,
    source: AnalyticsUploadSource = 'picker',
    attempt: UploadAnalyticsAttempt = { source, started: false, settled: false }
  ) {
    if (this.isUploading) {
      return;
    }

    const extraFiles = this.multiple() ? Array.from(files).slice(1) : [];

    this.startUpload(files, attempt, {
      upload: file => this.uploadService.uploadFile(file),
      inputElement: () => this.fileInput().nativeElement,
      onUploaded: extraFiles.length ? () => this.extraFilesSelected.emit(extraFiles) : undefined,
    });
  }

  /** Uploads the first file of the list through the local-file endpoint. */
  handleLocalFileInput(files: ArrayLike<File>) {
    if (this.isUploading) {
      return;
    }

    const extraFiles = this.multiple() ? Array.from(files).slice(1) : [];

    this.startUpload(
      files,
      { source: 'local_picker', started: false, settled: false },
      {
        upload: file => this.uploadService.uploadLocalFile(file),
        inputElement: () => this.localFileInput()?.nativeElement,
        onUploaded: extraFiles.length ? () => this.extraFilesSelected.emit(extraFiles) : undefined,
      }
    );
  }

  private startUpload(
    files: ArrayLike<File>,
    attempt: UploadAnalyticsAttempt,
    options: {
      upload: (file: File) => Observable<UploadInfo>;
      inputElement: () => HTMLInputElement | undefined;
      onUploaded?: () => void;
    }
  ): void {
    const fileToUpload = files?.[0];
    if (!fileToUpload || this.isUploading) {
      return;
    }

    if (!attempt.started) {
      attempt.started = true;
      this.analytics.track('file_upload_start', {
        tool_slug: this.toolSlug(),
        upload_source: attempt.source,
      });
    }

    const finish = () => {
      this.isUploading = false;
      this.uploadProcess.emit(false);
      const inputElement = options.inputElement();
      if (inputElement) inputElement.value = '';
    };

    this.isUploading = true;
    this.uploadProcess.emit(true);
    const operation: ErrorReportOperation = {
      operation: attempt.source === 'local_picker' ? 'read-image' : 'upload',
      ...(attempt.source === 'local_picker' ? {} : { endpoint: '/upload', method: 'POST' }),
      files: [{ name: fileToUpload.name, blob: fileToUpload }],
    };
    options.upload(fileToUpload).pipe(tap(info => assertSuccessfulOperationResponse(info.fileInfo))).subscribe({
      next: uploadInfo => {
        if (!attempt.settled && uploadInfo.state === 'DONE' && uploadInfo.fileInfo) {
          attempt.settled = true;
          this.analytics.track('file_upload_success', {
            tool_slug: this.toolSlug(),
            upload_source: attempt.source,
          });
          this.fileUploaded.emit(uploadInfo.fileInfo);
          options.onUploaded?.();
        }
      },
      error: error => {
        if (!attempt.settled) {
          attempt.settled = true;
          this.analytics.track('file_upload_error', {
            tool_slug: this.toolSlug(),
            upload_source: attempt.source,
            error_category: getEditorAnalyticsErrorCategory(error),
          });
          this.errorReporting.reportOperationError(error, operation);
        }
        finish();
      },
      complete: () => {
        if (!attempt.settled) {
          attempt.settled = true;
          this.analytics.track('file_upload_error', {
            tool_slug: this.toolSlug(),
            upload_source: attempt.source,
            error_category: 'unknown',
          });
          this.errorReporting.reportOperationError(new Error('Upload completed without a result.'), operation);
        }
        finish();
      },
    });
  }
}
